
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../../../store/slices/authSlice';
import { clearAuth } from '../../../store/slices/authSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useMediaQuery } from 'react-responsive';
import ChangePasswordScreen from './ChangePasswordScreen';
import EmailNotificationsScreen from './EmailNotificationsScreen';
import SupportTicketPanel from '../../SupportTickets/SupportTicketPanel';
import TicketNotificationBell from '../../SupportTickets/TicketNotificationBell';
import logo from '../../../assets/logo.png';
import './Header.css';
import {
  getPracticeAdminHomePath,
  getRoleDisplayName,
  getRoleId,
  isPracticeAdmin,
  isSinglePracticeScopedUser,
  ROLES,
} from '../../../utils/roleUtils';
import { hasChatSidebarAccess } from '../../../utils/chatPermissions';
import { buildPatientProfileImageUrl } from '../../../utils/patientProfileImage';
import patientService from '../../../services/patientService';

const Header = ({ user, onToggleSidebar, sidebarOpen }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1199 });
  const isDesktop = useMediaQuery({ minWidth: 1200 });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { breadcrumbs } = useBreadcrumb();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [activeScreen, setActiveScreen] = useState(null);
  const [supportPanelOpen, setSupportPanelOpen] = useState(false);
  const [notificationRefreshKey, setNotificationRefreshKey] = useState(0);
  const [patientProfilePic, setPatientProfilePic] = useState(null);
  const [patientProfileFailed, setPatientProfileFailed] = useState(false);
  const dropdownRef = useRef(null);
  const isPatient = getRoleId(user) === ROLES.PATIENT;
  const isSuperAdmin = getRoleId(user) === ROLES.SUPER_ADMIN;
  const patientProfilePicUrl = useMemo(
    () => buildPatientProfileImageUrl(patientProfilePic),
    [patientProfilePic]
  );
  const patientInitials = useMemo(() => {
    const first = String(user?.first_name || '').trim();
    const last = String(user?.last_name || '').trim();
    const fromName = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    if (fromName.trim()) return fromName;
    return String(user?.username || 'P').charAt(0).toUpperCase();
  }, [user?.first_name, user?.last_name, user?.username]);

  useEffect(() => {
    if (!isPatient) {
      setPatientProfilePic(null);
      setPatientProfileFailed(false);
      return undefined;
    }
    let cancelled = false;
    patientService.getCurrentPatient()
      .then((res) => {
        if (cancelled) return;
        const pic = res.data?.data?.profile_pic || res.data?.profile_pic || null;
        setPatientProfilePic(pic);
        setPatientProfileFailed(false);
      })
      .catch(() => {
        if (!cancelled) setPatientProfilePic(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isPatient, user?.id]);

  useEffect(() => {
    if (activeScreen || supportPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const handleScreenEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveScreen(null);
        setSupportPanelOpen(false);
      }
    };

    document.addEventListener('keydown', handleScreenEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleScreenEscape);
    };
  }, [activeScreen, supportPanelOpen]);

  // Logout must feel instant: clear local auth + navigate immediately,
  // then call backend logout in background.
  const handleLogout = () => {
    try {
      dispatch(clearAuth());
    } catch (e) {
      // ignore; still navigate
    }

    navigate('/login', { replace: true });

    // Fire and forget server-side logout (best effort).
    dispatch(logout()).catch((error) => {
      console.error('Logout error (ignored after navigation):', error);
    });
  };

  const getRoleName = (roleId) => getRoleDisplayName(roleId);

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleBreadcrumbClick = (breadcrumb) => {
    if (!breadcrumb.path) return;
    const path = String(breadcrumb.path);
    const qIndex = path.indexOf('?');
    if (qIndex >= 0) {
      navigate(
        {
          pathname: path.slice(0, qIndex) || location.pathname,
          search: path.slice(qIndex),
        },
        breadcrumb.state ? { state: breadcrumb.state } : undefined
      );
      return;
    }
    navigate(path, breadcrumb.state ? { state: breadcrumb.state } : undefined);
  };

  // User-manual info button temporarily hidden.
  // const handleHelpClick = () => {
  //   const cacheBust = Date.now();
  //   window.open(
  //     `/assets/eVitals-User-Manual.pdf?t=${cacheBust}#page=1`,
  //     '_blank',
  //     'noopener,noreferrer'
  //   );
  // };

  const openScreen = (screen) => {
    if (screen === 'emailNotifications' && !isSuperAdmin) return;
    setActiveScreen(screen);
    setShowUserDropdown(false);
  };

  const closeScreen = () => setActiveScreen(null);

  const openSupportPanel = () => {
    setSupportPanelOpen(true);
    setShowUserDropdown(false);
  };

  const openSupportTicketsScreen = () => {
    navigate('/support-tickets');
    setShowUserDropdown(false);
  };

  const handleTicketSubmitted = () => {
    setNotificationRefreshKey((value) => value + 1);
  };

  const rawBreadcrumbItems = Array.isArray(breadcrumbs) ? breadcrumbs : [];
  const hiddenBreadcrumbPaths = [
    '/practices',
    '/practices/add',
    '/users',
    '/admin',
    '/admin/add',
    '/system-caregiver-dashboard',
    '/system-caregiver-dashboard/add-system-caregiver',
    '/care-programs',
    '/care-programs/add',
    '/settings',
    '/billing',
    '/billing/sent-reports',
    '/reports',
    '/inventory'
  ];
  const breadcrumbItems = (() => {
    // Single-practice users: show practice name instead of a link to the global list.
    if (isSinglePracticeScopedUser(user) && rawBreadcrumbItems.length >= 2) {
      const [first, second, ...rest] = rawBreadcrumbItems;
      if (first?.label === 'Practices' && second?.label) {
        return [
          {
            ...first,
            label: second.label,
            path: null,
            active: false
          },
          ...rest
        ];
      }
    }
    return rawBreadcrumbItems;
  })();
  const normalizedPath = (location.pathname || '').replace(/\/+$/, '') || '/';
  const showBreadcrumbs = breadcrumbItems.length > 0 && !hiddenBreadcrumbPaths.includes(normalizedPath);

  const handleNavigate = (path) => {
    navigate(path);
    setShowUserDropdown(false);
  };

  const handleLogoClick = () => {
    if (isPracticeAdmin(user)) {
      navigate(getPracticeAdminHomePath(user));
      return;
    }
    navigate('/dashboard');
  };

  return (
    <header className="top-header" id="TopHeader">
      <div className={`header-left ${showBreadcrumbs ? '' : 'header-left--compact'}`}>
        <div className="logo-container" style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
          <img src={logo} alt="E-Vitals Logo" style={{ maxHeight: '65px', cursor: 'pointer' }} onClick={handleLogoClick} />
        </div>

        {showBreadcrumbs && (
          <div className="breadcrumb-wrapper">
            <nav aria-label="Breadcrumb" className="breadcrumb-nav">
              <ol className="breadcrumb">
                {breadcrumbItems.map((breadcrumb, index) => (
                  <React.Fragment key={index}>
                    <li
                      className={`breadcrumb-item ${breadcrumb.active ? 'active' : ''}`}
                      aria-current={breadcrumb.active ? 'page' : undefined}
                    >
                      {breadcrumb.active || !breadcrumb.path ? (
                        <span className="breadcrumb-current">
                          {breadcrumb.label}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="breadcrumb-link"
                          onClick={() => handleBreadcrumbClick(breadcrumb)}
                        >
                          {breadcrumb.label}
                        </button>
                      )}
                    </li>
                    {index < breadcrumbItems.length - 1 && (
                      <li className="breadcrumb-separator" aria-hidden="true">
                        <i className="fa fa-chevron-right"></i>
                      </li>
                    )}
                  </React.Fragment>
                ))}

              </ol>
            </nav>
          </div>
        )}
      </div>

      <div className="header-right">
        {/* User-manual info button temporarily hidden.
        <button
          className="header-icon-btn"
          type="button"
          aria-label="Open user manual"
          onClick={handleHelpClick}
        >
          <i className="fa fa-info-circle" aria-hidden="true"></i>
        </button>
        */}

        <TicketNotificationBell
          key={notificationRefreshKey}
          onOpenTickets={openSupportTicketsScreen}
          userId={user?.id}
          includeChat={hasChatSidebarAccess(user?.role_id)}
        />

        <div
          className="user-info"
          ref={dropdownRef}
          onClick={() => setShowUserDropdown(!showUserDropdown)}
        >
          {isPatient && patientProfilePicUrl && !patientProfileFailed ? (
            <img
              className="user-info-avatar"
              src={patientProfilePicUrl}
              alt=""
              onError={() => setPatientProfileFailed(true)}
            />
          ) : (
            <i className="fa fa-user" aria-hidden="true"></i>
          )}
          <span className="user-text">
            {user?.username}
          </span>
          <i
            className={`fa fa-caret-${showUserDropdown ? 'up' : 'down'}`}
            style={{ marginLeft: '6px' }}
          ></i>

          {showUserDropdown && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span>User Profile</span>
              </div>

              <div className="dropdown-profile">
                <div className="profile-avatar">
                  {isPatient && patientProfilePicUrl && !patientProfileFailed ? (
                    <img
                      src={patientProfilePicUrl}
                      alt={user?.username || 'Patient profile'}
                      onError={() => setPatientProfileFailed(true)}
                    />
                  ) : isPatient ? (
                    <span className="profile-avatar-initials">{patientInitials}</span>
                  ) : (
                    <img src="/assets/images/user.png" alt="Profile avatar" />
                  )}
                </div>
                <div className="profile-details">
                  <span className="profile-name">{user?.username || 'User'}</span>
                  <span className="profile-role">{getRoleName(user?.role_id)}</span>
                </div>
              </div>

              <ul className="dropdown-links">
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => handleNavigate('/settings')}
                  >
                    <i className="fa fa-cog" aria-hidden="true"></i>
                    <span>Setting</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => openScreen('changePassword')}
                  >
                    <i className="fa fa-lock" aria-hidden="true"></i>
                    <span>Change Password</span>
                  </button>
                </li>
                {isSuperAdmin && (
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => openScreen('emailNotifications')}
                    >
                      <i className="fa fa-envelope-open" aria-hidden="true"></i>
                      <span>Email Notifications Settings</span>
                    </button>
                  </li>
                )}
                {Number(user?.role_id) !== 1 && (
                  <li>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={openSupportPanel}
                    >
                      <i className="fa fa-life-ring" aria-hidden="true"></i>
                      <span>Submit Support Ticket</span>
                    </button>
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={openSupportTicketsScreen}
                  >
                    <i className="fa fa-ticket" aria-hidden="true"></i>
                    <span>{Number(user?.role_id) === 1 ? 'Support Ticket Admin' : 'My Support Tickets'}</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="dropdown-item logout-item"
                    onClick={handleLogout}
                  >
                    <i className="fa fa-sign-out" aria-hidden="true"></i>
                    <span>Logout</span>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      {activeScreen === 'changePassword' && (
        <ChangePasswordScreen onClose={closeScreen} />
      )}
      {isSuperAdmin && activeScreen === 'emailNotifications' && (
        <EmailNotificationsScreen onClose={closeScreen} />
      )}
      <SupportTicketPanel
        open={Number(user?.role_id) !== 1 && supportPanelOpen}
        onClose={() => setSupportPanelOpen(false)}
        onSubmitted={handleTicketSubmitted}
      />
    </header>
  );
};

export default Header;

